import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { EngagementBillingDetail, ServiceFlatFeeDetail, AgentBillingBreakdown } from '../types/billing';
import {
  formatBillingCurrency,
  formatBillingHoursDisplay,
  sumAndRoundHalfUp2,
} from './billingRound';
import { renderPatchingPdfSections } from './patchingBillingPdfSection';
import { isResourceChargeExportRow } from '../constants/patchingGroups';
import { TENARAI_LOGO_BASE64 } from './tenaraiLogoBase64';

const formatCurrency = formatBillingCurrency;
const formatHours = formatBillingHoursDisplay;

// Get billing period date range
const getBillingPeriodDates = (billingPeriod: string) => {
  const [year, month] = billingPeriod.split('-').map(Number);
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };
  return {
    fromDate: formatDate(firstDay),
    toDate: formatDate(lastDay),
    monthYear: firstDay.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
  };
};

interface BillingPdfData {
  detail: EngagementBillingDetail;
  billingPeriod: string;
  companyName?: string;
  companyLogo?: string;
}

export const generateBillingPdf = (data: BillingPdfData): void => {
  const { detail, billingPeriod } = data;
  const { fromDate, toDate, monthYear } = getBillingPeriodDates(billingPeriod);
  const currency = detail.engagement.currency || 'USD';

  // Create PDF document
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const tableWidth = pageWidth - 2 * margin;
  const footerAreaHeight = 35;
  const maxContentY = pageHeight - footerAreaHeight;
  let yPosition = 20;
  const pulseLineY = yPosition;

  // Header background - 100% width
  const headerHeight = 50;
  doc.setFillColor(249, 250, 251); // Light gray background
  doc.rect(0, 0, pageWidth, headerHeight, 'F');

  // Header - "Pulse" and tagline on left side
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bolditalic');
  doc.setTextColor(31, 41, 55);
  doc.text('Pulse', margin, yPosition);

  // Tagline
  yPosition += 6;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(107, 114, 128);
  doc.text('Intelligent Cloud Monitoring & Remediation Platform', margin, yPosition);

  // Header - Tenarai branding on right, text aligned with Pulse on same line
  const tenaraiLogoSize = 16;
  const tenaraiBlockRight = pageWidth - margin;
  doc.setFontSize(22);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(75, 85, 99);
  const tenaraiText = 'Tenarai';
  const tenaraiTextWidth = doc.getTextWidth(tenaraiText);
  doc.text(tenaraiText, tenaraiBlockRight - tenaraiTextWidth, pulseLineY);

  try {
    const tenaraiLogoX = tenaraiBlockRight - tenaraiTextWidth + (tenaraiTextWidth - tenaraiLogoSize) / 2;
    doc.addImage(
      TENARAI_LOGO_BASE64,
      'PNG',
      tenaraiLogoX,
      pulseLineY - 2,
      tenaraiLogoSize,
      tenaraiLogoSize
    );
  } catch (e) {
    // If logo fails to load, continue without it
    console.warn('Failed to load logo:', e);
  }

  // Title - smaller font
  yPosition += 10;
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  doc.text('Billing Statement for Engagement', margin, yPosition);

  // Billing Period - single line with smaller text in parentheses
  yPosition += 6;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(107, 114, 128);
  
  const mainText = `Billing Period: ${monthYear} `;
  doc.text(mainText, margin, yPosition);
  
  const mainTextWidth = doc.getTextWidth(mainText);
  const parentheticalX = margin + mainTextWidth;
  
  doc.setFontSize(9);
  const parentheticalText = `(${fromDate} - ${toDate})`;
  doc.text(parentheticalText, parentheticalX, yPosition);

  // Customer Info Boxes (BILL TO, ENGAGEMENT, and TOTAL DUE side by side)
  yPosition += 6;
  const boxesY = yPosition;
  const boxHeight = 18;
  const boxGap = 5;
  const billToBoxWidth = tableWidth * 0.35;
  const engagementBoxWidth = tableWidth * 0.35;
  const totalDueBoxWidth = tableWidth * 0.25;

  // BILL TO box - 40% width on left
  const billToBoxX = margin;
  doc.setFillColor(229, 237, 255); // Light blue background
  doc.roundedRect(billToBoxX, boxesY, billToBoxWidth, boxHeight, 3, 3, 'F');

  let currentY = boxesY + 7;
  doc.setFontSize(10);
  doc.setTextColor(107, 114, 128);
  doc.text('BILL TO', billToBoxX + 5, currentY);

  currentY += 6;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  doc.text(detail.engagement.customerName, billToBoxX + 5, currentY);

  // ENGAGEMENT box - 40% width in middle
  const engagementBoxX = billToBoxX + billToBoxWidth + boxGap;
  doc.setFillColor(229, 237, 255); // Light blue background (same as BILL TO)
  doc.roundedRect(engagementBoxX, boxesY, engagementBoxWidth, boxHeight, 3, 3, 'F');

  currentY = boxesY + 7;
  doc.setFontSize(10);
  doc.setTextColor(107, 114, 128);
  doc.text('ENGAGEMENT', engagementBoxX + 5, currentY);

  currentY += 6;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  doc.text(detail.engagement.name, engagementBoxX + 5, currentY);

  // Total Due Box - 20% width on right
  const totalDueBoxX = engagementBoxX + engagementBoxWidth + boxGap;
  doc.setFillColor(34, 197, 94);
  doc.roundedRect(totalDueBoxX, boxesY, totalDueBoxWidth, boxHeight, 3, 3, 'F');

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(255, 255, 255);
  doc.text('TOTAL DUE', totalDueBoxX + 5, boxesY + 6);

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(formatCurrency(detail.summary?.finalBill || 0, currency), totalDueBoxX + 5, boxesY + 13);

  // Export-only: Sphera Solutions – filter L1 Support Engineer and hide hours in total row & summary
  const isSpheraExport = (detail.engagement.customerName || '').trim() === 'Sphera Solutions';
  const L1_GROUP_NAME = 'L1 Support Engineer';

  // Resource Charges Section
  yPosition = boxesY + boxHeight + 10;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  doc.text('Resource Charges', margin, yPosition);

  yPosition += 5;

  // Agent breakdown table (for Sphera export: exclude L1 Support Engineer rows)
  const agentBreakdownForExport =
    detail.agentBreakdown?.filter((a: AgentBillingBreakdown) =>
      isResourceChargeExportRow(a.groupName, { excludeL1ForSphera: isSpheraExport })
    ) ?? [];

  if (agentBreakdownForExport.length > 0) {
    const totalAmount = isSpheraExport
      ? sumAndRoundHalfUp2(agentBreakdownForExport.map((a) => a.billableAmount))
      : sumAndRoundHalfUp2([detail.summary?.totalBillableAmount || 0]);

    const agentTableData = agentBreakdownForExport.map((agent: AgentBillingBreakdown) => [
      agent.userName,
      agent.groupName,
      formatHours(agent.totalHours),
      formatCurrency(agent.hourlyRate, currency) + '/hr',
      formatCurrency(agent.billableAmount, currency)
    ]);

    // Total row: hide total hours in Resource Charges (PDF only); show amount only
    agentTableData.push([
      'Total',
      '',
      '',
      '',
      formatCurrency(totalAmount, currency)
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [['Resource', 'Group', 'Hours', 'Rate', 'Amount']],
      body: agentTableData,
      theme: 'striped',
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 10
      },
      bodyStyles: {
        fontSize: 9,
        textColor: [31, 41, 55]
      },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'left' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right', fontStyle: 'bold' }
      },
      margin: { left: margin, right: margin, bottom: footerAreaHeight },
      tableWidth: tableWidth,
      didParseCell: (data: any) => {
        // Right-align header cells for Hours, Rate, and Amount
        if (data.section === 'head' && data.column.index >= 2) {
          data.cell.styles.halign = 'right';
        }
        // Style the total row (last row)
        if (data.section === 'body' && data.row.index === agentTableData.length - 1) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fontSize = 10;
          data.cell.styles.fillColor = [243, 244, 246];
          data.cell.styles.textColor = [31, 41, 55];
        }
      }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;
    if (yPosition > maxContentY) {
      doc.addPage();
      yPosition = margin;
    }
  } else {
    yPosition += 10;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(107, 114, 128);
    doc.text('No resource charges for this period', margin, yPosition);
    yPosition += 15;
  }

  const patchingLinesForPdf = (detail.patchingServices || [])
    .filter((line) => line.showInPeriod !== false)
    .map((line) => ({
      ...line,
      engagementNumber:
        (line as { engagementNumber?: string }).engagementNumber ?? detail.engagement.number
    }));
  if (patchingLinesForPdf.length > 0) {
    yPosition = renderPatchingPdfSections({
      doc,
      lines: patchingLinesForPdf,
      currency,
      margin,
      tableWidth,
      footerAreaHeight,
      maxContentY,
      startY: yPosition
    });
  }

  // Service Plans Section (if applicable)
  if (detail.serviceFlatFeeDetails && detail.serviceFlatFeeDetails.length > 0) {
    yPosition += 5;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 41, 55);
    doc.text('Service Plans', margin, yPosition);

    yPosition += 5;

    const planTableData = detail.serviceFlatFeeDetails.map((plan: ServiceFlatFeeDetail) => [
      plan.planName,
      formatCurrency(plan.price, currency)
    ]);

    autoTable(doc, {
      startY: yPosition,
      head: [['Plan', 'Amount']],
      body: planTableData,
      theme: 'striped',
      headStyles: {
        fillColor: [139, 92, 246], // Purple
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 10
      },
      bodyStyles: {
        fontSize: 9,
        textColor: [31, 41, 55]
      },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'right', fontStyle: 'bold' }
      },
      margin: { left: margin, right: margin, bottom: footerAreaHeight },
      tableWidth: tableWidth,
      didParseCell: (data: any) => {
        // Right-align header cell for Amount column
        if (data.section === 'head' && data.column.index === 1) {
          data.cell.styles.halign = 'right';
        }
      }
    });

    yPosition = (doc as any).lastAutoTable.finalY + 10;
    if (yPosition > maxContentY) {
      doc.addPage();
      yPosition = margin;
    }
  }

  // Billing Summary Section
  yPosition += 5;
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 41, 55);
  doc.text('Billing Summary', margin, yPosition);

  yPosition += 5;

  // Calculate service fee differential
  const minimumFee = detail.summary?.minimumServiceFee || 0;
  const actualHoursEffort = detail.summary?.totalBillableAmount || 0;
  const serviceFeeCharge = minimumFee - actualHoursEffort;

  // Build summary rows (for Sphera export: no Total Hours in Billing Summary, keep amounts only)
  const summaryRows: (string | number)[][] = [];

  const resourceChargesDetails = isSpheraExport
    ? ''
    : `${formatHours(detail.summary?.totalHours ?? 0)} hrs`;
  const resourceChargesAmount = detail.summary?.totalBillableAmount ?? 0;
  summaryRows.push([
    'Resource Charges',
    resourceChargesDetails,
    formatCurrency(resourceChargesAmount, currency)
  ]);

  // Service Fee (only if applicable)
  if (serviceFeeCharge > 0) {
    summaryRows.push([
      'Service Fee (Minimum Fee Adjustment)',
      '',
      formatCurrency(serviceFeeCharge, currency)
    ]);
  }

  // Platform Cost (if applicable)
  if ((detail.summary?.platformCost || 0) > 0) {
    summaryRows.push([
      'Platform Cost',
      '',
      formatCurrency(detail.summary?.platformCost || 0, currency)
    ]);
  }

  // Service Flat Fee (if applicable)
  if ((detail.summary?.serviceFlatFee || 0) > 0) {
    summaryRows.push([
      'Service Plans',
      '',
      formatCurrency(detail.summary?.serviceFlatFee || 0, currency)
    ]);
  }

  if ((detail.summary?.patchingTotalAmount || 0) > 0) {
    summaryRows.push([
      'Patching Services (Resource Costs)',
      '',
      formatCurrency(detail.summary.patchingTotalAmount || 0, currency)
    ]);
  }

  // Add Total row
  summaryRows.push([
    'Total',
    '',
    formatCurrency(detail.summary?.finalBill || 0, currency)
  ]);

  autoTable(doc, {
    startY: yPosition,
    head: [['Description', isSpheraExport ? '' : 'Details', 'Amount']],
    body: summaryRows,
    theme: 'plain',
    headStyles: {
      fillColor: [243, 244, 246],
      textColor: [107, 114, 128],
      fontStyle: 'bold',
      fontSize: 9
    },
    bodyStyles: {
      fontSize: 10,
      textColor: [31, 41, 55]
    },
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'right' },
      2: { halign: 'right' }
    },
    margin: { left: margin, right: margin, bottom: footerAreaHeight },
    tableWidth: tableWidth,
    didParseCell: (data: any) => {
      // Right-align header cells for Details and Amount columns
      if (data.section === 'head' && data.column.index >= 1) {
        data.cell.styles.halign = 'right';
      }
      // Highlight the last row (Total) with background color and make it bold
      if (data.section === 'body' && data.row.index === summaryRows.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fontSize = 11;
        data.cell.styles.fillColor = [229, 237, 255];
        data.cell.styles.textColor = [31, 41, 55];
      }
    }
  });

  // Footer - add to all pages in the reserved footer area
  const totalPages = (doc as any).internal.pages.length - 1;
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    
    // Footer background - 100% width
    const footerStartY = pageHeight - footerAreaHeight;
    doc.setFillColor(249, 250, 251);
    doc.rect(0, footerStartY, pageWidth, footerAreaHeight, 'F');
    
    const currentFooterY = pageHeight - footerAreaHeight + 10;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(156, 163, 175);
    
    doc.text('This is a system-generated billing statement from Tenarai Pulse.', margin, currentFooterY);
    doc.text(
      `Generated on ${new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })}`,
      margin,
      currentFooterY + 5
    );
    
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 20, currentFooterY + 8);
  }

  // Save the PDF
  const customerName = detail.engagement.customerName.replace(/[^a-zA-Z0-9]/g, '_');
  const engagementNumber = detail.engagement.number;
  const filename = `Billing-${customerName}-${engagementNumber}-${billingPeriod}.pdf`;
  doc.save(filename);
};

export default generateBillingPdf;
